import { useState, useRef, useEffect } from 'react';
import { useAgents } from '../../lib/use-agents';
import { useSessions } from '../../lib/use-sessions';
import { Bot, Plus, History, ChevronRight, Edit, Trash2, Loader2, ArrowLeft, MessageSquare, MoreHorizontal, Cpu } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { SectionHeader } from '../ui/section-header';
import type { Agent } from '../../lib/agent-types';
import { AgentDeletionDialog } from './AgentDeletionDialog';

/** Build a display string for the model badge on an agent card */
function getModelDisplayTag(agent: Agent): string | null {
  const providerLabel = agent.providerTemplate?.name;
  const modelLabel = agent.providerModel?.name;
  if (!providerLabel && !modelLabel) return null;

  if (providerLabel && modelLabel) return `${providerLabel} · ${modelLabel}`;
  return providerLabel || modelLabel || null;
}

// ─── Component ─────────────────────────────────────────────

interface AgentSelectionPageProps {
  workspaceId: string | null;
  onAgentSelect: (agent: Agent) => void;
  onSessionSelect: (sessionId: string, agentId?: string | null) => void;
  onCreateAgent: () => void;
  onEditAgent: (agent: Agent) => void;
}

export function AgentSelectionPage({
  workspaceId,
  onAgentSelect,
  onSessionSelect,
  onCreateAgent,
  onEditAgent,
}: AgentSelectionPageProps) {
  const { agents, loading: agentsLoading, deleteAgent, loadAgents } = useAgents(workspaceId);
  const { sessions, loading: sessionsLoading } = useSessions(workspaceId);
  const [showHistory, setShowHistory] = useState(false);
  const [menuAgentId, setMenuAgentId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    if (!menuAgentId) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuAgentId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuAgentId]);

  const handleDeleteClick = (e: React.MouseEvent, agent: Agent) => {
    e.stopPropagation();
    setMenuAgentId(null);
    setDeleteTarget(agent);
  };

  const handleDeleteConfirm = async (agent: Agent) => {
    setDeletingId(agent.id);
    try {
      await deleteAgent(agent.id);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete agent:', err);
    } finally {
      setDeletingId(null);
    }
  };

  if (agentsLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="size-6 text-primary animate-spin mb-3" />
        <p className="text-sm text-muted-foreground">Loading agents...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <SectionHeader
        leading={showHistory ? (
          <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)} className="h-7 w-7 p-0 shrink-0">
            <ArrowLeft className="size-4" />
          </Button>
        ) : null}
        icon={showHistory ? <History className="size-4 text-primary" /> : <Bot className="size-4 text-primary" />}
        title={showHistory ? 'History' : 'Agents'}
        subtitle={showHistory ? 'Recent conversations' : 'Select or create an agent'}
        footer={
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {showHistory
                ? `${sessions.length} conversation${sessions.length !== 1 ? 's' : ''}`
                : `${agents.length} agent${agents.length !== 1 ? 's' : ''}`
              }
            </p>
            <div className="flex gap-1">
              {!showHistory && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setShowHistory(true)} className="h-7 px-2 text-xs">
                    <History className="size-3.5" />
                    <span className="hidden min-[340px]:inline ml-1">History</span>
                  </Button>
                  <Button size="sm" onClick={onCreateAgent} className="h-7 px-2 text-xs">
                    <Plus className="size-3.5" />
                    <span className="hidden min-[340px]:inline ml-1">New Agent</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        }
      />

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {showHistory ? (
          /* ─── History View ──────────────────────── */
          sessionsLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="size-6 text-primary animate-spin mb-3" />
              <p className="text-sm text-muted-foreground">Loading history...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex items-center justify-center size-14 rounded-2xl bg-muted mb-4">
                <History className="size-7 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium mb-1">No conversations yet</p>
              <p className="text-xs text-muted-foreground text-center max-w-[240px]">
                Start chatting with an agent to see your conversation history here.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {sessions.slice(0, 20).map((session) => (
                <button
                  key={session.id}
                  className={`w-full text-left border rounded-lg bg-card p-3 transition-colors hover:bg-accent/40 ${session.executionStatus === 'ARCHIVED' ? 'opacity-60' : ''}`}
                  onClick={() => onSessionSelect(session.id, session.assistantId)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center size-8 rounded-md bg-muted shrink-0">
                      <MessageSquare className="size-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium truncate">{session.displayName || 'Untitled Chat'}</span>
                        {session.executionStatus === 'ARCHIVED' && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">Archived</Badge>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {session.updatedAt ? new Date(session.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                    <ChevronRight className="size-4 text-muted-foreground shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )
        ) : (
          /* ─── Agents List ──────────────────────── */
          agents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex items-center justify-center size-14 rounded-2xl bg-muted mb-4">
                <Bot className="size-7 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium mb-1">No agents yet</p>
              <p className="text-xs text-muted-foreground mb-5 text-center max-w-[240px]">
                Create your first agent to start chatting with AI.
              </p>
              <Button size="sm" onClick={onCreateAgent}>
                <Plus className="size-3.5 mr-1.5" />
                Create Agent
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {agents.map((agent) => {
                const modelTag = getModelDisplayTag(agent);
                return (
                  <div
                    key={agent.id}
                    className={`group relative border rounded-xl bg-card transition-all hover:shadow-md hover:border-primary/30 cursor-pointer ${
                      deletingId === agent.id ? 'opacity-50 pointer-events-none' : ''
                    }`}
                    onClick={() => onAgentSelect(agent)}
                  >
                    <div className="p-2.5">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center size-11 rounded-xl bg-primary/8 shrink-0">
                          <Bot className="size-4 text-primary" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold truncate leading-tight">{agent.name}</span>
                            {modelTag && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted/60 rounded-md px-1.5 py-0.5 max-w-[180px] truncate">
                                <Cpu className="size-2.5" />
                                <span className="truncate">{modelTag}</span>
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="relative shrink-0" ref={menuAgentId === agent.id ? menuRef : undefined}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuAgentId(menuAgentId === agent.id ? null : agent.id);
                            }}
                            className="size-8 flex items-center justify-center rounded-lg text-muted-foreground/70 hover:text-foreground hover:bg-accent transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                          >
                            <MoreHorizontal className="size-4" />
                          </button>

                          {menuAgentId === agent.id && (
                            <div className="absolute right-0 top-9 z-30 w-[170px] bg-popover border rounded-lg shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                              <div className="py-1">
                                <button
                                  className="w-full text-left px-3 py-2 text-xs hover:bg-accent/60 flex items-center gap-2.5 transition-colors text-foreground"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuAgentId(null);
                                    onEditAgent(agent);
                                  }}
                                >
                                  <Edit className="size-3.5 text-muted-foreground" />
                                  Edit Agent
                                </button>
                              </div>
                              <div className="h-px bg-border" />
                              <div className="py-1">
                                <button
                                  className="w-full text-left px-3 py-2 text-xs flex items-center gap-2.5 transition-colors text-destructive hover:bg-destructive/10"
                                  onClick={(e) => handleDeleteClick(e, agent)}
                                >
                                  <Trash2 className="size-3.5" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Create new agent row */}
              <button
                className="w-full border border-dashed rounded-xl p-4 flex items-center gap-3 text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-all h-14"
                onClick={onCreateAgent}
              >
                <div className="flex items-center justify-center size-9 rounded-lg bg-muted shrink-0">
                  <Plus className="size-4" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium">New Agent</p>
                </div>
              </button>
              <div className="h-[45vh]" />
            </div>
          )
        )}
      </div>
      {deleteTarget && (
        <AgentDeletionDialog
          agent={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirmDelete={() => handleDeleteConfirm(deleteTarget)}
        />
      )}
    </div>
  );
}
